import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { COMPANY } from "../data/company";
import Logo from "./Logo";
import { EASE } from "./Reveal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const solid = scrolled || open;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          solid
            ? "border-b border-navy-900/10 bg-white/85 shadow-[0_12px_40px_rgba(11,19,43,0.1)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="wrap flex h-[76px] items-center justify-between">
          <Link to="/" data-testid="navbar-logo-link" aria-label="BIK COMMOTRADE — home" onClick={() => setOpen(false)}>
            <Logo />
          </Link>

          <nav className="hidden items-center gap-5 2xl:gap-6 lg:flex" aria-label="Primary">
            {COMPANY.nav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `relative whitespace-nowrap py-2 text-[12px] font-medium uppercase tracking-[0.12em] transition-colors duration-300 ${
                    isActive ? "text-brand-emeraldDark" : "text-slate-600 hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="navbar-enquire-now-button"
              className="btn btn-primary btn-sm hidden whitespace-nowrap sm:inline-flex"
            >
              Enquire Now
              <ArrowUpRight className="h-4 w-4" />
            </Link>

            <button
              type="button"
              data-testid="navbar-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="relative flex h-11 w-11 items-center justify-center rounded-full border border-navy-900/15 bg-navy-900/[0.03] backdrop-blur-md transition hover:border-brand-emeraldLight/60 lg:hidden"
            >
              <span
                className={`absolute h-[1.5px] w-5 bg-ink transition-all duration-300 ${
                  open ? "rotate-45" : "-translate-y-[4px]"
                }`}
              />
              <span
                className={`absolute h-[1.5px] w-5 bg-ink transition-all duration-300 ${
                  open ? "-rotate-45" : "translate-y-[4px]"
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-white/[0.97] pt-[92px] backdrop-blur-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="wrap flex flex-1 flex-col gap-1 overflow-y-auto pb-6" aria-label="Mobile">
              {COMPANY.nav.map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `group flex items-center justify-between border-b border-navy-900/[0.08] py-4 font-display text-3xl font-bold uppercase tracking-wide ${
                        isActive ? "text-brand-emeraldDark" : "text-ink"
                      }`
                    }
                  >
                    {item.label}
                    <ArrowUpRight className="h-5 w-5 text-brand-emeraldDark opacity-0 transition group-hover:opacity-100" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="wrap space-y-3 pb-10"
            >
              <Link to="/contact" onClick={() => setOpen(false)} className="btn btn-primary w-full">
                Enquire Now
              </Link>
              <div className="flex flex-col gap-2 pt-2 text-sm text-slate-500">
                {COMPANY.phones.map((p) => (
                  <a key={p.link} href={`tel:${p.link}`} className="inline-flex items-center gap-2">
                    <Phone className="h-4 w-4 text-brand-emeraldDark" /> {p.display}
                  </a>
                ))}
                <a href={`mailto:${COMPANY.emails[1]}`} className="inline-flex items-center gap-2">
                  <Mail className="h-4 w-4 text-brand-emeraldDark" /> {COMPANY.emails[1]}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
